import React, { useMemo, useState, useEffect } from 'react';

import { Field as FormikField, useFormikContext } from 'formik';
import { useHistory, useRouteMatch, useLocation } from 'react-router-dom';
import { Select, Option } from '@xo-union/tk-component-fields';
import { Checkbox } from '@xo-union/tk-component-switches';

import { Field, Textarea } from '../../../../libs/gs-component-form';
import RightRail, {
  Content,
  Footer,
  Header,
  TripleAction,
  Confirmation,
} from '../../../../libs/gs-component-right-rail';

import { generateMessagingPath } from '../../../../helpers/generateMessagingPath';
import styles from '../../../SharePage/rightRails.scss';
import {
  maxEmailMessageLength,
  maxLinkButtonTextLength,
  maxSubjectLength,
  maxUrlLength,
} from '../../../SharePage/sharingValidation';
import useShareTracking from '../../../SharePage/useShareTracking';
import { useMessagingSequencesQuery, useWeddingWebsiteQuery } from '../../../../generated/graphql';
import {
  getSequenceMap,
  getSaveTheDateEligibility,
  SAVE_THE_DATE_MEDIA_ID,
  getSequences,
  SEQUENCE_TYPE_RSVP_SCHEDULE,
  SEQUENCE_TYPE_LOCAL_HOTELS,
  Sequence,
} from '../../messagingHubHelper';
import WeddingInfoModal from '../../WeddingInfoModal';
import { useMobileMedia } from '@xo-union/tk-ui-breakpoints';
import { messagingHubRoute } from '../../../../helpers/routes';
import InfoCard from '../../InfoCard';
import { LOOKING_FOR_HOTELS_INFO_CARD } from '../../../SharePage/helper';

interface ShareValues {
  subject: string;
  message: string;
  linkButtonText: string;
}

interface Props {
  customUrl?: boolean;
  noLinkButton?: boolean;
  matchPath: string;
  displayName?: string;
  wwsCheckboxText?: string;
  showLinkButton?: boolean;
  setShowLinkButton?: React.Dispatch<React.SetStateAction<boolean>>;
  initialValues: ShareValues;
}

const LOCAL_HOTEL_EMAIL_SEQUENCE_ID = '28ec5e00-39d4-49d0-8d1e-24aefb4d8ad0';

const EmailEditorRail: React.FC<Props> = ({
  customUrl = false,
  noLinkButton = false,
  matchPath,
  displayName = 'Share Your Website',
  wwsCheckboxText = 'Include a link to our website',
  showLinkButton = true,
  initialValues,
  setShowLinkButton,
}) => {
  const { data, loading } = useMessagingSequencesQuery();
  const { data: gdsData, loading: gdsLoading } = useWeddingWebsiteQuery();
  const { dirty, isValid, handleReset, setFieldValue } = useFormikContext();
  const match = useRouteMatch(matchPath);
  const history = useHistory();
  const { pathname } = useLocation();
  const parts = pathname.split('/');
  const [showModal, setShowModal] = useState(false);
  const [showAbandonChangesConfirmation, setShowAbandonChangesConfirmation] = useState(false);
  const [currentSequenceId, setCurrentSequenceId] = useState(parts[parts.length - 2]);
  const [targetSequenceId, setTargetSequenceId] = useState('');

  const sequences = useMemo(() => getSequences(data, gdsData), [gdsData, data]);
  const sequenceMap: Map<string, string> = useMemo(() => getSequenceMap(sequences, 'email'), [sequences]);
  const track = useShareTracking();
  const mobileMedia = useMobileMedia();

  function doneHandler() {
    if (isValid) {
      if (dirty) {
        track('WWS Share Interaction', { action: 'edit message' });
      }
      const nextLoc = pathname.replace('/edit', '');
      history.push(nextLoc);
    }
  }

  function cancelHandler() {
    history.push(messagingHubRoute);
  }

  function handleSequenceChangeHelper(selectedSequenceId: string) {
    const { member, wedding } = gdsData ?? {};
    const mediaId = sequenceMap.get(selectedSequenceId);

    if (SAVE_THE_DATE_MEDIA_ID === mediaId && !getSaveTheDateEligibility(member, wedding)) {
      setShowModal(true);
      setTargetSequenceId(selectedSequenceId);
    } else {
      setShowModal(false);
      setCurrentSequenceId(selectedSequenceId);
    }

    if (SAVE_THE_DATE_MEDIA_ID === mediaId) {
      //Save the date selection tracking
      track('Save the Date Interaction', {
        action: 'Save the Date selected',
        messageType: 'Save the date',
        medium: 'email',
      });
    }

    handleReset();
  }

  function handleSequenceChange(event: any) {
    let sequenceId: string = event.option.value;
    if (!dirty && showLinkButton) {
      handleSequenceChangeHelper(sequenceId);
    } else {
      setShowAbandonChangesConfirmation(true);
      setTargetSequenceId(sequenceId);
    }
  }

  function onCancel() {
    setShowAbandonChangesConfirmation(false);
  }

  function onConfirm() {
    handleSequenceChangeHelper(targetSequenceId);
    setShowAbandonChangesConfirmation(false);
  }

  function handleSubmit(sequenceId: string) {
    setShowModal(false);
    setCurrentSequenceId(sequenceId);
  }

  useEffect(() => {
    sequences.active.forEach((s: Sequence) => {
      if (s?.supportedTypes?.find((b: { type: string }) => b.type === 'email')?.id === currentSequenceId) {
        const to = generateMessagingPath(s.sequenceType, currentSequenceId, 'email');
        history.push(to);
      }
    });
  }, [currentSequenceId, history, sequences]);

  const headerProps = mobileMedia.yes ? { leftButtonText: 'Back', leftButtonOnClick: cancelHandler } : {};

  return (
    <>
      <RightRail open={!!match} overlay={false} className={styles.rightRail}>
        <Header {...headerProps}>New email</Header>
        {!showAbandonChangesConfirmation ? (
          <>
            <Content className={styles.editorContainer}>
              {(loading || gdsLoading) && !data && !currentSequenceId ? (
                <div></div>
              ) : (
                <div className={styles.templateContainer}>
                  <Select name="Template" value={currentSequenceId} onChange={(e: any) => handleSequenceChange(e)}>
                    {sequences.active.map((s: Sequence) => {
                      let displayName = s.displayName;
                      // TODO: Delete After test
                      if (s.sequenceType === SEQUENCE_TYPE_RSVP_SCHEDULE) {
                        return null;
                      }
                      if (s.sequenceType === SEQUENCE_TYPE_LOCAL_HOTELS) {
                        displayName = 'Share your hotel block';
                      }

                      let emailSeqId = s?.supportedTypes?.find((b: { type: string }) => b.type === 'email')?.id;

                      return (
                        <Option key={displayName} value={emailSeqId}>
                          {displayName}
                        </Option>
                      );
                    })}
                  </Select>
                </div>
              )}

              {showModal ? (
                <WeddingInfoModal
                  onSubmit={sequenceId => handleSubmit(sequenceId as string)}
                  onClose={() => setShowModal(false)}
                  sequence={targetSequenceId}
                />
              ) : null}

              <FormikField
                name="subject"
                component={Field}
                maxLength={maxSubjectLength}
                data-testid="email-editor-rail-subject"
              />
              <FormikField
                name="message"
                component={Textarea}
                maxLength={maxEmailMessageLength}
                data-testid="email-editor-rail-message"
              />
              {!noLinkButton && currentSequenceId === SAVE_THE_DATE_MEDIA_ID && setShowLinkButton ? (
                <div className={styles.toggleWWSSaveTDate}>
                  <Checkbox
                    datatest-id="wedding-website-email-button-toggle"
                    checked={showLinkButton}
                    onChange={() => {
                      if (showLinkButton) {
                        setFieldValue('linkButtonText', initialValues.linkButtonText);
                      }
                      setShowLinkButton(!showLinkButton);
                    }}
                  >
                    {wwsCheckboxText}
                  </Checkbox>
                </div>
              ) : null}
              {!noLinkButton ? (
                <FormikField
                  name="linkButtonText"
                  label="Button Text"
                  component={Field}
                  disabled={!showLinkButton}
                  maxLength={maxLinkButtonTextLength}
                  data-testid="wedding-website-email-button-text"
                />
              ) : null}
              {customUrl ? (
                <FormikField
                  name="customUrl"
                  label="Livestream URL"
                  component={Field}
                  maxLength={maxUrlLength}
                  data-testid="email-editor-rail-livestream-url"
                />
              ) : null}
              {currentSequenceId === LOCAL_HOTEL_EMAIL_SEQUENCE_ID ? (
                <div className={styles.localHotelInfoCard}>
                  <InfoCard
                    {...LOOKING_FOR_HOTELS_INFO_CARD}
                    onClick={(track: ReturnType<typeof useShareTracking>) =>
                      LOOKING_FOR_HOTELS_INFO_CARD.onClick(track, 'email')
                    }
                  />
                </div>
              ) : null}
            </Content>
            <Footer>
              <TripleAction
                primaryOnClick={doneHandler}
                primaryText="Add Recipients"
                primaryDisabled={!isValid}
                secondaryOnClick={cancelHandler}
                secondaryText="Cancel"
              />
            </Footer>
          </>
        ) : (
          <Confirmation
            data-testid="changes-confirmation"
            question="Are you sure?"
            info="Any unsaved changes will be lost."
            primaryText="Yes"
            onPrimaryAction={onConfirm}
            secondaryText="No"
            onSecondaryAction={onCancel}
            showSpinner={loading}
          />
        )}
      </RightRail>
    </>
  );
};

export default EmailEditorRail;
